import {
  NoteNumberQualifier,
  NoteTableCellType,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import type { ConsultationStatus } from '@/shared/api/transcriptor';
import type { BadgeTone } from '@/shared/ui';

import type {
  DocumentType,
  NoteTextLimits,
  NoteValueType,
} from '@/entities/templates';

import type { TemplateEditState } from '../lib';

import type { NoteBlockPreset, NoteFieldSetting } from './types';

export const ENCOUNTER_STATUS_TONES: Record<ConsultationStatus, BadgeTone> = {
  created: 'neutral',
  recording: 'danger',
  processing: 'violet',
  succeeded: 'success',
  failed: 'danger',
};

export const IDLE_TEMPLATE_EDIT: TemplateEditState = {
  editing: false,
  source: null,
  draft: null,
  issues: [],
  documentIssues: [],
};

export const EMPTY_BLOCK_PRESET: NoteBlockPreset = {
  values: [],
  text: '',
  teeth: [],
  teethGroups: [],
};

export const CONSULTATION_DOCUMENT_TYPES: DocumentType[] = [
  'SUMMARY',
  'EVALUATION',
];

// The codes an evaluation's section scoring the transcript itself is stored
// under in the community templates.
export const TRANSCRIPT_QUALITY_SECTION_CODES = [
  'stage7_transcript_analysis',
  'transcript_quality',
];

// Options show at most this many values in the line; the rest open from
// "More…" with a search.
export const NOTE_OPTIONS_VISIBLE = 12;

// An empty number box is never narrower than this many characters; a typed
// value sets its width and grows it.
export const NOTE_NUMBER_MIN_CHARS = 5;

// The library's templates are read in one page; it holds dozens.
export const LIBRARY_PAGE_SIZE = 200;

// Scribe's length limits (model.NoteFreeTextMaxLength, NoteOverrideMaxLength,
// NoteLineNoteMaxLength): a longer text is refused, never cut, and with it
// every other change of the same update.
export const NOTE_FREE_TEXT_MAX_LENGTH = 1000;
export const NOTE_OVERRIDE_MAX_LENGTH = 2000;
export const NOTE_LINE_NOTE_MAX_LENGTH = 1000;

// The note's document stops each text at the limit scribe holds it to; a
// line Enter added is a wording.
export const NOTE_TEXT_LIMITS: NoteTextLimits = {
  wording: NOTE_OVERRIDE_MAX_LENGTH,
  freeText: NOTE_FREE_TEXT_MAX_LENGTH,
  lineNote: NOTE_LINE_NOTE_MAX_LENGTH,
  // A value typed over a chosen one is an own value.
  choiceValue: NOTE_FREE_TEXT_MAX_LENGTH,
};

// Scribe's limit of own values on a block that takes several
// (model.NoteOwnValuesMaxCount): one more is refused with the whole update.
export const NOTE_OWN_VALUES_MAX_COUNT = 20;

export const PARTNER_EXPORT_DELAY_MS = 500;

// A tab switch fires the window's focus and the page's visibility together.
export const TAB_RETURN_DEDUPE_MS = 1000;

// Scribe's number print by template language (`note_language.go`): the
// decimal separator, the labels of its units (UCUM codes) and of a tooth's
// surfaces. A language without them prints the stored forms.
const LATIN_SURFACE_LETTERS: Record<string, string> = {
  mesial: 'M',
  distal: 'D',
  vestibular: 'B',
  oral: 'L',
  occlusal: 'O',
  incisal: 'I',
};

export const NOTE_NUMBER_LANGUAGES: Record<
  string,
  {
    decimalSeparator: string;
    unitLabels: Record<string, string>;
    surfaceLetters: Record<string, string>;
    // What a value of a number field is for, by NoteNumberQualifier.
    qualifierLabels: Record<number, string>;
  }
> = {
  en: {
    decimalSeparator: '.',
    unitLabels: {
      mm: 'mm',
      cm: 'cm',
      mL: 'ml',
      deg: '°',
      '%': '%',
      d: 'd',
      wk: 'wk',
      mo: 'mo',
      a: 'y',
      '1': '',
    },
    surfaceLetters: LATIN_SURFACE_LETTERS,
    qualifierLabels: {
      [NoteNumberQualifier.NoteNumberQualifierRight]: 'right',
      [NoteNumberQualifier.NoteNumberQualifierLeft]: 'left',
      [NoteNumberQualifier.NoteNumberQualifierUpper]: 'upper',
      [NoteNumberQualifier.NoteNumberQualifierLower]: 'lower',
      [NoteNumberQualifier.NoteNumberQualifierFront]: 'front',
    },
  },
  ru: {
    decimalSeparator: ',',
    unitLabels: {
      mm: 'мм',
      cm: 'см',
      mL: 'мл',
      deg: '°',
      '%': '%',
      d: 'дн.',
      wk: 'нед.',
      mo: 'мес.',
      a: 'г.',
      '1': '',
    },
    surfaceLetters: {
      mesial: 'М',
      distal: 'Д',
      vestibular: 'В',
      oral: 'О',
      occlusal: 'Ж',
      incisal: 'Р',
    },
    qualifierLabels: {
      [NoteNumberQualifier.NoteNumberQualifierRight]: 'справа',
      [NoteNumberQualifier.NoteNumberQualifierLeft]: 'слева',
      [NoteNumberQualifier.NoteNumberQualifierUpper]: 'сверху',
      [NoteNumberQualifier.NoteNumberQualifierLower]: 'снизу',
      [NoteNumberQualifier.NoteNumberQualifierFront]: 'спереди',
    },
  },
  de: {
    decimalSeparator: ',',
    unitLabels: {
      mm: 'mm',
      cm: 'cm',
      mL: 'ml',
      deg: '°',
      '%': '%',
      d: 'T.',
      wk: 'Wo.',
      mo: 'Mon.',
      a: 'J.',
      '1': '',
    },
    surfaceLetters: LATIN_SURFACE_LETTERS,
    qualifierLabels: {
      [NoteNumberQualifier.NoteNumberQualifierRight]: 'rechts',
      [NoteNumberQualifier.NoteNumberQualifierLeft]: 'links',
      [NoteNumberQualifier.NoteNumberQualifierUpper]: 'oben',
      [NoteNumberQualifier.NoteNumberQualifierLower]: 'unten',
      [NoteNumberQualifier.NoteNumberQualifierFront]: 'vorne',
    },
  },
};

// The units a number takes (UCUM codes, scribe's `NoteUnits`).
export const NOTE_UNITS = [
  'mm',
  'cm',
  'mL',
  'deg',
  '%',
  'd',
  'wk',
  'mo',
  'a',
  '1',
];

// Off: the images presentation shows no pictures, and its options read as
// text as any other.
export const NOTE_OPTION_IMAGES_SHOWN = false;

// The pictures this client draws for the images presentation.
export const NOTE_OPTION_IMAGES = [
  'profile.straight',
  'profile.convex',
  'profile.concave',
  'arch_form.parabola',
  'arch_form.v_shaped',
  'arch_form.trapezoid',
  'arch_form.triangular',
  'arch_form.saddle',
  'arch_form.asymmetric',
];

// The types a table column's cells take, in the order they are offered.
export const NOTE_TABLE_CELL_TYPES = [
  NoteTableCellType.NoteTableCellTypeCheckbox,
  NoteTableCellType.NoteTableCellTypeDate,
  NoteTableCellType.NoteTableCellTypeText,
  NoteTableCellType.NoteTableCellTypeNumber,
  NoteTableCellType.NoteTableCellTypeTooth,
];

export const NOTE_NUMBER_QUALIFIERS = [
  NoteNumberQualifier.NoteNumberQualifierRight,
  NoteNumberQualifier.NoteNumberQualifierLeft,
  NoteNumberQualifier.NoteNumberQualifierUpper,
  NoteNumberQualifier.NoteNumberQualifierLower,
  NoteNumberQualifier.NoteNumberQualifierFront,
];

// The word scribe prints for a field's explicit none without a label of its
// own, by template language (`output_language.go`).
export const NOTE_NONE_WORDS: Record<string, string> = {
  en: 'none',
  ru: 'нет',
  de: 'keine',
};

// Scribe prints a date field in English with the month's name.
export const NOTE_DATE_ENGLISH_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Scribe's table of the field settings each value type allows
// (`noteValueTypeSettings`); the parity test holds both to it.
export const NOTE_VALUE_TYPE_SETTINGS: Record<
  NoteValueType,
  NoteFieldSetting[]
> = {
  text: ['slot_id', 'default', 'ai_fill', 'link'],
  choice: [
    'slot_id',
    'default',
    'allow_none',
    'ai_fill',
    'dictation_rule',
    'link',
  ],
  teeth: [
    'slot_id',
    'default',
    'allow_none',
    'ai_fill',
    'dictation_rule',
    'link',
  ],
  number: ['slot_id', 'default', 'ai_fill', 'link'],
  date: ['slot_id', 'default', 'ai_fill', 'link'],
  table: ['slot_id', 'default', 'ai_fill', 'dictation_rule', 'link'],
};
